import { Component, OnInit, Inject } from '@angular/core';
import { _, isNotNullOrUndefined } from '@icc/shared/helpers';
import { PageComponent } from '@icc/configurator/shared';
import { SharedFacade, ICC_PAGE_DATA } from '@icc/configurator/shared';
import { iccListItem, iccListTab } from '@icc/configurator/ui';
import { IccAccessoryAccessory, IccAccessoryColor } from '@icc/construction-elements/types';
import { ModalService } from '@icc/configurator/shared';
import { HardwareTypeOptionsPageComponent } from '../hardware-type-options-page/hardware-type-options-page.component';
import { ConfigurationsService } from '@icc/configurator/shared';
import { ConfiguratorsDataService, EventBusService, TranslateService } from '@icc/shared';
import { KeysService } from '../cylinders-page/keys.service';
import { ColorMappingService } from '@icc/construction-elements/configurator';

@Component({
    selector: 'icc-hardware-types-page',
    templateUrl: './hardware-types-page.component.html',
    styleUrls: ['./hardware-types-page.component.scss'],
    standalone: false
})
export class HardwareTypesPageComponent extends PageComponent implements OnInit {
    public title = _('DOOR|Wybierz typ osprzętu');
    public options = [];
    public selected: any = null;

    types: iccListItem[] = [];
    tabs: iccListTab[] = [];
    selectedTypeId = 0;
    constructor(
        private sharedFacade: SharedFacade,
        private eventBusService: EventBusService,
        @Inject(ICC_PAGE_DATA)
        private pageData: {
            title: string;
            types: IccAccessoryAccessory[];
            hardwareColors?: IccAccessoryColor[];
            selType : number;
            selColor: IccAccessoryColor;
            tabs?: iccListTab[];
            canOpenOptions?: (item: IccAccessoryAccessory) => boolean;
            colorIdToMapping?: null | number;
            defaultImageUrl?: null | string
        },
        private modalService: ModalService,
        private translateService: TranslateService,
        private keysService: KeysService,
        private configurationsService: ConfigurationsService<'door'>,
        private configuratorsDataService: ConfiguratorsDataService,
        private colorMappingService: ColorMappingService,
    ) {
        super();
        this.title = pageData.title;
        this.eventBusService.post({ key: 'processDependencies', value: null });
    }

    ngOnInit() {
        this.types = this.pageData.types.map(type => {
            const selectedColor = this.getSelectedColor(type);
            return {
                id: Number(type.id),
                title: type.name,
                description: type.description,
                imageUrl: this.getImageUrl(type, selectedColor),
                colors: this.pageData.hardwareColors == null ? [] :
                    type.colors_ids
                    && type.colors_ids
                        .map(c =>
                            this.pageData.hardwareColors.find(color => Number(c) === Number(color.id))
                        )
                        .filter(isNotNullOrUndefined)
                        .map(color => ({
                            id: Number(color.id),
                            name: color.name,
                            color: color.color,
                            colorImg: color.color_img,
                        })) || [],
                selectedColor,
                showColorSelection: false,
                tabs: this.pageData.tabs && this.getTabs(type),
            };
        });
        this.selectedTypeId = Number(this.pageData.selType);

        const dependencies = this.configuratorsDataService.data.dependencies || [];
        const blockedItems = this.configurationsService.conf.Current.Dependencies.filter(
            (p) => p.type === 'blockade_to_configuration' || p.type === 'blockadeAccessory'
        );
        if (
            Array.isArray(dependencies) &&
            blockedItems.length > 0 &&
            Array.isArray(blockedItems) &&
            dependencies.length > 0
        ) {
            this.markAsDisabled(blockedItems, dependencies, 'pull');
            this.markAsDisabled(blockedItems, dependencies, 'accessory');
        }
    }

    markAsDisabled(blockedItems, dependencies, accessoryType) {
        dependencies.forEach(dependency => {
            blockedItems.forEach(blocked => {
                if (dependency && blocked && dependency.id === Number(String(blocked.id).split('-')[0])) {
                    if(blocked.type === 'blockadeAccessory') {
                        const accessoriesId = dependency.actions.find((p) => p.type === 'blockadeAccessory')?.value || [];
                        accessoriesId.forEach((id) => {
                            this.disablePullAndAddDescription(id, accessoryType);

                        });
                    } else {
                        dependency.conditions.forEach(condition => {
                            if (condition?.type === accessoryType) {
                                const type = this.types.find((p) => p.id === Number(condition?.value));
                                if (type && condition?.value) {
                                    this.disablePullAndAddDescription(condition?.value, accessoryType);
                                }
                            }
                        })
                    }
                }
            });
        });
    }

    disablePullAndAddDescription(id, accessoryType: 'pull' | 'accessory') {
        const type = this.types.find((p) => p.id === Number(id));
        if(!type) {
            return;
        }
        type.disabled = true;

        switch (accessoryType) {
            case 'pull': {
                type.description = this.translateService.instant('ACCESSORY|Nie można dodać pochwytu do konstrukcji');
                break;
            }
            case 'accessory': {
                type.description = this.translateService.instant('ACCESSORY|Osprzęt nie może zostać dodany do konstrukcji');
                break;
            }
        }
    }

    private getImageUrl(hardware: IccAccessoryAccessory, selectedColor: number): string {
        if(hardware.colors_imgs || hardware.img) {
            return hardware.colors_imgs && hardware.colors_imgs[selectedColor]
                ? '/files/windowhandlescolorswindowaccessory/' + hardware.colors_imgs[selectedColor]
                : '/files/windowaccessory/' + hardware.img;
        } else {
            return this.pageData.defaultImageUrl; 
        }
    }

    selectType(item: iccListItem) {
        const type = this.pageData.types.find(f => Number(f.id) === Number(item.id));

        if(type.type === 'cylinder') {
            this.keysService.resetQuantityIfKeysAreDifferent(type);
        }

        if (type && type.id && (typeof this.pageData.canOpenOptions !== 'function' || this.pageData.canOpenOptions(type))) {
            this.modalService.open({
                pageComponent: HardwareTypeOptionsPageComponent,
                resolve:{
                    selectedType: type,
                    hardwareColors: this.pageData.hardwareColors ? this.pageData.hardwareColors : [],
                    selColor: this.pageData.selColor,
                    colorIdToMapping: this.pageData.colorIdToMapping
                }
            }).result.then((result) => {
                this.sharedFacade.closePage(Object.assign(result, {
                    type
                }));
            });
        } else {
            this.sharedFacade.closePage({
                type
            });
        }
    }

    private getTabs(type) {
        if (type.type === 'plate') {
            this.tabs = this.pageData.tabs.map(tab => ({
                    id: tab.toString(),
                    name: this.getPlateNames(tab)
            }));
            return [type.plate_type];
        } else if (type.type === 'handle' && ['pull', 'doublePull'].indexOf(type.handle_type) > -1) {
            this.tabs = this.pageData.tabs.map((tab) => ({
                id: tab.id,
                name: tab.name,
            }));
            return [type.window_accessories_category_id];
        } else {
            return [];
        }
    }

    private getPlateNames(tab) {
        const tabNames = {
            long: this.translateService.instant('DOOR|Długi'),
            divided: this.translateService.instant('DOOR|Dzielony'),
            bottom: this.translateService.instant('DOOR|Dolny'),
        };
        for (const [key, name] of Object.entries(tabNames)) {
            if(key === tab) {
                return name;
            }
        }
    }

    private getSelectedColor(type) {
        if(this.pageData.colorIdToMapping) {
            const matchedColors = this.colorMappingService.getMappedColorsFromAccessory(this.pageData.colorIdToMapping);
            let matchedColor = [];
            matchedColor = matchedColors?.filter((el) =>
                type.colors_ids?.some((p) => Number(p) === Number(el))
            );
            if (matchedColor && matchedColor[0]) {
               return matchedColor[0];
            }
        }
        return (type.colors_ids
            && ((this.pageData.selColor
                && Number(
                    type.colors_ids.find(
                        id => Number(this.pageData.selColor.id) === Number(id)
                    )
                ))
                || Number(type.colors_ids[0])))
        || 0;
    }
}
